/**
 * Trace Extractor
 *
 * @module @chatsdet/diagnostic-workspace/trace-extractor
 * @description Pure-transform side of trace handling. The data-fetch layer
 * (server) downloads trace.zip from S3 to a local path, then calls into here
 * with that path. The CLI does the same with a signed-URL download.
 *
 * Writes:
 *   - `browser/trace.zip` — copied to the workspace as an escape hatch
 *   - `browser/actions.jsonl` — parsed action list
 *   - `browser/doms/<NNNN>-<absoluteTsMs>.html` — composed self-contained HTML
 *   - `browser/frames/<NNNN>-<absoluteTsMs>.png` — screenshot per action
 *   - `browser/a11y/<NNNN>-<absoluteTsMs>.yaml` — agent's accessibility view
 *
 * Reconstruction launches a headless Playwright browser via
 * `@chatsdet/trace-tools`'s `reconstructSnapshots`. Soft-fails if the browser
 * can't start; the workspace is marked partial.
 */

import * as fs from 'node:fs/promises';
import {
  parseTraceActions,
  reconstructSnapshots,
  type TraceAction,
  type SnapshotResult,
} from '@chatsdet/trace-tools';
import {
  actionArtifactBasename,
  resolveWorkspacePath,
  WORKSPACE_PATHS,
} from './paths.js';
import { makeTraceClock, type TraceClock } from './_shared.js';
import { writeScreencast, type KeptScreencastFrame } from './screencast-extractor-writer.js';

export interface TraceExtractorInput {
  workspaceRoot: string;
  /** Local path to the run's trace.zip. Caller is responsible for fetching it. */
  traceZipPath: string | null;
  /** Anchor for converting trace timestamps to the run-start axis. */
  runStartedAtMs: number | null;
  /** Absolute ms of every step-started/step-completed annotation (screencast keep-density). */
  stepBoundaryMs?: ReadonlyArray<number>;
  /** The failed step's work window (absolute ms) — screencast kept at full density here. */
  failureWindow?: { startMs: number; endMs: number } | null;
}

export interface TraceWriteResult {
  /** True if anything failed (workspace marked partial). */
  partial: boolean;
  /** Kept screencast frames, for the step-rollup builder to interleave by timestamp. */
  screencastFrames: KeptScreencastFrame[];
}

export const writeTrace = async (input: TraceExtractorInput): Promise<TraceWriteResult> => {
  if (!input.traceZipPath) {
    await writePartialMarker(input.workspaceRoot, 'no-trace');
    return { partial: true, screencastFrames: [] };
  }

  // Copy the trace.zip into the workspace as an escape hatch.
  const destTraceZip = resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.browserTraceZip);
  try {
    await fs.copyFile(input.traceZipPath, destTraceZip);
  } catch {
    await writePartialMarker(input.workspaceRoot, 'trace-copy-failed');
    return { partial: true, screencastFrames: [] };
  }

  let actions: TraceAction[];
  try {
    actions = parseTraceActions(input.traceZipPath);
  } catch {
    await writePartialMarker(input.workspaceRoot, 'trace-parse-failed');
    return { partial: true, screencastFrames: [] };
  }

  // One clock for both lanes — actions and screencast frames land on the same
  // absolute axis the ledger/rollups/inWindow already use.
  const clock = makeTraceClock(actions, input.runStartedAtMs);

  await writeActionsJsonl(input.workspaceRoot, actions, clock);

  // Screencast runs purely from the zip and MUST run even if DOM reconstruction
  // soft-fails — that's the exact case (evaluate-only steps) that left runs with
  // no visual evidence. Independent dirs → run both concurrently.
  const [reconPartial, screencast] = await Promise.all([
    reconstructAndWriteArtifacts({
      workspaceRoot: input.workspaceRoot,
      tracePath: input.traceZipPath,
      actions,
      clock,
    }),
    writeScreencast({
      workspaceRoot: input.workspaceRoot,
      tracePath: input.traceZipPath,
      actions,
      clock,
      stepBoundaryMs: input.stepBoundaryMs ?? [],
      failureWindow: input.failureWindow ?? null,
    }),
  ]);

  return {
    partial: reconPartial || screencast.partial,
    screencastFrames: screencast.frames,
  };
};

const writePartialMarker = async (workspaceRoot: string, reason: string): Promise<void> => {
  await fs.writeFile(
    resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.browserActions),
    `${JSON.stringify({ partial: true, reason })}\n`,
    'utf-8',
  );
};

interface ReconstructInput {
  workspaceRoot: string;
  tracePath: string;
  actions: ReadonlyArray<TraceAction>;
  clock: TraceClock;
}

const reconstructAndWriteArtifacts = async (input: ReconstructInput): Promise<boolean> => {
  const computeAbsoluteTs = (action: TraceAction): number => input.clock.absFromAction(action);

  let results: SnapshotResult[];
  try {
    results = await reconstructSnapshots({
      traceZipPath: input.tracePath,
      filter: 'after',
      includeScreenshots: true,
      includeHtml: true,
      raw: false,
    });
  } catch {
    return true;
  }

  const byActionIndex = new Map<number, SnapshotResult>();
  for (const result of results) {
    if (!byActionIndex.has(result.actionIndex)) {
      byActionIndex.set(result.actionIndex, result);
    }
  }

  const domsDir = resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.browserDoms);
  const framesDir = resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.browserFrames);
  const a11yDir = resolveWorkspacePath(input.workspaceRoot, WORKSPACE_PATHS.browserA11y);

  // For each action, run the three writes (HTML/PNG/YAML) concurrently — distinct
  // paths, no contention. Then run the per-action work concurrently across all
  // actions. Per-action failures don't block siblings; we track partial-state.
  const writeAction = async (i: number): Promise<boolean> => {
    const action = input.actions[i]!;
    const result = byActionIndex.get(i);
    if (!result) return false;
    const tsMs = computeAbsoluteTs(action);
    try {
      await Promise.all([
        result.html
          ? fs.writeFile(
              `${domsDir}/${actionArtifactBasename(i, tsMs, 'html')}`,
              result.html,
              'utf-8',
            )
          : Promise.resolve(),
        result.screenshotBuffer
          ? fs.writeFile(
              `${framesDir}/${actionArtifactBasename(i, tsMs, 'png')}`,
              result.screenshotBuffer,
            )
          : Promise.resolve(),
        result.yaml
          ? fs.writeFile(
              `${a11yDir}/${actionArtifactBasename(i, tsMs, 'yaml')}`,
              result.yaml,
              'utf-8',
            )
          : Promise.resolve(),
      ]);
      return true;
    } catch {
      return false;
    }
  };

  const indexes = Array.from({ length: input.actions.length }, (_, i) => i);
  const missingResults = indexes.filter((i) => !byActionIndex.has(i)).length;
  const writeOutcomes = await Promise.all(indexes.map((i) => writeAction(i)));
  const writeFailures = writeOutcomes.filter((ok) => !ok).length;
  return missingResults > 0 || writeFailures > 0;
};

const writeActionsJsonl = async (
  workspaceRoot: string,
  actions: ReadonlyArray<TraceAction>,
  clock: TraceClock,
): Promise<void> => {
  const traceBase = actions.length > 0 ? actions[0]!.startTime : 0;
  const lines = actions.map((a, idx) => ({
    actionIndex: idx,
    callId: a.callId,
    title: a.title,
    startTimeRelativeMs: a.startTime - traceBase,
    endTimeRelativeMs: a.endTime - traceBase,
    // Absolute run-start axis — shared with the screencast filmstrip so frames
    // and actions correlate by `timestampMs` with no cross-clock math.
    timestampMs: clock.absFromAction(a),
    // Raw wall-clock epoch (the screencast frames' native clock) for auditability.
    wallTimeMs: a.wallTime ?? null,
    params: a.params ?? null,
    beforeSnapshot: a.beforeSnapshot ?? null,
    afterSnapshot: a.afterSnapshot ?? null,
    inputSnapshot: a.inputSnapshot ?? null,
    pageId: a.pageId,
    error: a.error ?? null,
    point: a.point ?? null,
  }));
  await fs.writeFile(
    resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.browserActions),
    lines.length === 0 ? '' : `${lines.map((l) => JSON.stringify(l)).join('\n')}\n`,
    'utf-8',
  );
};
